import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, Loader2, Calendar, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  scrapReportApiService, 
  DailyScrapSummaryDto
} from '../services/scrapReportApi';
import { inventoryReportApiService, MaterialIssueReportDto } from '../services/inventoryReportApi';
import { getScrapReportConfig } from '../config/uiConfig';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Cell
} from 'recharts';

/** Convert scrap weight from grams to tons */
const SCRAP_GRAMS_TO_TON = 1 / 1_000_000;

const ScrapReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const scrapReportConfig = getScrapReportConfig();
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [scrapData, setScrapData] = useState<DailyScrapSummaryDto[]>([]);
  const [materialIssueData, setMaterialIssueData] = useState<MaterialIssueReportDto[]>([]);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [loadingEndgoods, setLoadingEndgoods] = useState(false);
  const [selectedEndgoodName, setSelectedEndgoodName] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState<string | null>(null);
  const [selectedDayDetails, setSelectedDayDetails] = useState<DailyScrapSummaryDto[]>([]);

  // Date range picker - default to current month
  const now = new Date();
  const getCurrentMonthStart = () => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };
  
  const getCurrentMonthEnd = () => {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState<string>(getCurrentMonthStart());
  const [endDate, setEndDate] = useState<string>(getCurrentMonthEnd());

  // Load endgoods for filter
  const loadEndgoods = async () => {
    setLoadingEndgoods(true);
    try {
      const response = await endgoodApiService.getEndgoodsIdName();
      if (response.success && Array.isArray(response.data)) {
        setEndgoods(response.data);
      }
    } catch (error) {
      // console.error('Error loading endgoods:', error);
    } finally {
      setLoadingEndgoods(false);
    }
  };

  useEffect(() => {
    loadEndgoods();
  }, []);

  useEffect(() => {
    if (selectedBranch || user?.defaultBranchId) {
      if (startDate && endDate) {
        loadScrapData();
      }
    }
  }, [selectedBranch, user?.defaultBranchId, startDate, endDate]);

  const loadScrapData = async () => {
    const branchId = selectedBranch || user?.defaultBranchId;
    
    if (!branchId) {
      toast.error('Please select a branch to view the scrap report');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    setLoading(true);
    try {
      const [scrapResponse, materialIssueResponse] = await Promise.all([
        scrapReportApiService.getScrapReportByDateRange(startDate, endDate, branchId),
        inventoryReportApiService.getMaterialIssueReportByBranch(branchId, startDate, endDate),
      ]);

      if (scrapResponse.success && scrapResponse.data) {
        const data = Array.isArray(scrapResponse.data) ? scrapResponse.data : [];
        setScrapData(data);
      } else {
        toast.error(scrapResponse.message || 'Failed to load scrap data');
        setScrapData([]);
      }

      if (materialIssueResponse.success && materialIssueResponse.data) {
        setMaterialIssueData(Array.isArray(materialIssueResponse.data) ? materialIssueResponse.data : []);
      } else {
        setMaterialIssueData([]);
      }
    } catch (error) {
      toast.error('Failed to load report data');
      setScrapData([]);
      setMaterialIssueData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadScrapDetails = async (productionDate: string) => {
    const branchId = selectedBranch || user?.defaultBranchId;
    
    if (!branchId) {
      toast.error('Please select a branch');
      return;
    }

    setLoadingDetails(true);
    try {
      // Use the date range API with the same date for start and end
      const response = await scrapReportApiService.getScrapReportByDateRange(
        productionDate,
        productionDate,
        branchId
      );
      
      if (response.success && response.data) {
        setSelectedDayDetails(response.data || []);
      } else {
        toast.error(response.message || 'Failed to load scrap details');
        setSelectedDayDetails([]);
      }
    } catch (error) {
      // console.error('Error loading scrap details:', error);
      toast.error('Failed to load scrap details');
      setSelectedDayDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Get available endgood names from scrap details
  const availableEndgoodNames = useMemo(() => {
    // We need to get endgood names from the scrap details
    // For now, we'll use the endgoods list we loaded
    return endgoods
      .filter(eg => eg.isActive)
      .map(eg => eg.endgoodName)
      .sort();
  }, [endgoods]);

  // Use scrap data directly since API already filters by date range
  const filteredScrapData = useMemo(() => {
    return scrapData;
  }, [scrapData]);

  // Filter material issue data by configured category (e.g. "Raw Material") for chart and summary
  const filteredMaterialIssueData = useMemo(() => {
    const category = scrapReportConfig?.materialIssueCategory?.trim();
    if (!category) return materialIssueData;
    const match = category.toLowerCase();
    return materialIssueData.filter(
      (item) => (item.rawCategoryName?.trim().toLowerCase() ?? '') === match
    );
  }, [materialIssueData, scrapReportConfig?.materialIssueCategory]);

  // Prepare chart data: merge scrap (scrapWeight in grams → tons) and material issue by date
  const chartData = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const showMonth = diffDays > 31 || start.getMonth() !== end.getMonth();

    const daysData = Array.from({ length: diffDays }, (_, i) => {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const day = currentDate.getDate();
      const dateLabel = showMonth
        ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : day.toString();
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      return {
        day,
        date: dateLabel,
        fullDate: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        scrapWeight: 0,
        materialIssued: 0,
        dateObj: currentDate,
        dateKey,
      };
    });

    const toDayOnly = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

    // Scrap: use scrapWeight (grams) → tons
    (filteredScrapData || []).forEach((row) => {
      if (row.productionDate) {
        try {
          const dayDate = new Date(row.productionDate);
          dayDate.setHours(0, 0, 0, 0);
          const dayIndex = daysData.findIndex((d) => d.dateObj && toDayOnly(d.dateObj) === toDayOnly(dayDate));
          if (dayIndex >= 0 && dayIndex < diffDays) {
            const grams = Number((row as { scrapWeight?: number }).scrapWeight) || 0;
            daysData[dayIndex].scrapWeight += grams * SCRAP_GRAMS_TO_TON;
          }
        } catch {
          // skip invalid row
        }
      }
    });

    // Material issued: from material issue report API (issueQuantity is in tons), filtered by config category
    filteredMaterialIssueData.forEach((item) => {
      if (!item.issueDate) return;
      try {
        let issueDate: Date;
        if (item.issueDate.includes(' ')) {
          const [datePart] = item.issueDate.split(' ');
          const [y, m, d] = datePart.split('-').map(Number);
          issueDate = new Date(y, m - 1, d);
        } else {
          issueDate = new Date(item.issueDate);
        }
        if (isNaN(issueDate.getTime())) return;
        const dayIndex = daysData.findIndex((d) => d.dateObj && toDayOnly(d.dateObj) === toDayOnly(issueDate));
        if (dayIndex >= 0 && dayIndex < diffDays) {
          daysData[dayIndex].materialIssued += Number(item.issueQuantity) || 0;
        }
      } catch {
        // skip invalid row
      }
    });

    return daysData;
  }, [filteredScrapData, filteredMaterialIssueData, startDate, endDate]);

  // X-axis label step to avoid overlapping labels
  const xAxisLabelStep = useMemo(() => {
    const length = chartData.length || 0;
    if (length <= 15) return 1;
    return Math.ceil(length / 12);
  }, [chartData]);

  const handleBarClick = (entry: any) => {
    if (entry && entry.dateKey) {
      setSelectedDate(entry.dateKey);
      setSelectedDateLabel(entry.fullDate || entry.date || entry.dateKey);
      // Load details for the selected date
      loadScrapDetails(entry.dateKey);
    }
  };

  // Filter details: only rows with scrapWeight > 0, and by endgood name if selected
  const filteredDetails = useMemo(() => {
    let filtered = selectedDayDetails.filter((detail) => (detail.scrapWeight ?? 0) > 0);
    if (selectedEndgoodName) {
      filtered = filtered.filter(detail => detail.endgoodName === selectedEndgoodName);
    }
    return filtered;
  }, [selectedDayDetails, selectedEndgoodName]);

  // Distinct endgoods with no process weight (outputWeightPerUnit is null/undefined; 0 is valid)
  const distinctEndgoodsWithoutProcessWeight = useMemo(() => {
    const withoutWeight = scrapData.filter((row) => row.outputWeightPerUnit == null);
    const seen = new Set<string>();
    withoutWeight.forEach((row) => {
      const key = String(row.endgoodId ?? row.endgoodName ?? '');
      if (key) seen.add(key);
    });
    return seen.size;
  }, [scrapData]);

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatTonsPerThousand = (gramsPerUnit?: number): string => {
    if (!gramsPerUnit || isNaN(gramsPerUnit)) return '-';
    const tons = gramsPerUnit / 1000;
    return tons.toLocaleString('en-IN', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Trash2 className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Scrap Report</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadScrapData}
                  disabled={loading || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Content */}
        {!selectedBranch ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Trash2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">Please select a branch to view the report</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters - always visible when a branch is selected */}
            <Card className="border-primary-200 bg-white">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        Filter by Endgood Name:
                      </label>
                      <Select 
                        value={selectedEndgoodName || "all"} 
                        onValueChange={(value) => setSelectedEndgoodName(value === "all" ? "" : value)}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select endgood name" />
                        </SelectTrigger>
                        <SelectContentWithSearch searchPlaceholder="Search endgood name...">
                          <SelectItem value="all">All Endgoods</SelectItem>
                          {loadingEndgoods ? (
                            <SelectItem value="loading" disabled>Loading endgoods...</SelectItem>
                          ) : availableEndgoodNames.length === 0 ? (
                            <SelectItem value="no-endgoods" disabled>No endgoods found</SelectItem>
                          ) : (
                            availableEndgoodNames.map((endgoodName) => (
                              <SelectItem key={endgoodName} value={endgoodName}>
                                {endgoodName}
                              </SelectItem>
                            ))
                          )}
                        </SelectContentWithSearch>
                      </Select>
                    </div>
                    <div className="flex items-center gap-4 flex-1">
                      <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        Date Range:
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 whitespace-nowrap">From:</label>
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 whitespace-nowrap">To:</label>
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Main content: show loader / empty state / chart */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading report data...</span>
              </div>
            ) : filteredScrapData.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Trash2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No data available for this report</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try changing the date range or check back later
                  </p>
                </div>
              </div>
            ) : (
              <div>
                {distinctEndgoodsWithoutProcessWeight > 0 && (
                  <div className="mb-4 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                    <strong>{distinctEndgoodsWithoutProcessWeight}</strong> distinct endgood(s) in this period have no process weight (output weight per unit not set).
                  </div>
                )}
                <Card className="border-primary-200 bg-white shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-800">
                      Scrap Generation by Date
                      {startDate && endDate && (
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          - {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="w-full">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={chartData}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                          barCategoryGap="20%"
                          barGap={0}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11 }}
                            axisLine={{ stroke: '#e0e0e0' }}
                            tickLine={{ stroke: '#e0e0e0' }}
                            interval={0}
                            minTickGap={0}
                            tickFormatter={(value, index) => (index % xAxisLabelStep === 0 ? value : '')}
                          />
                          <YAxis 
                            label={{ value: 'Weight (Ton)', angle: -90, position: 'insideLeft' }}
                            tick={{ fontSize: 12 }}
                            axisLine={{ stroke: '#e0e0e0' }}
                            tickLine={{ stroke: '#e0e0e0' }}
                            tickFormatter={(value) => formatNumber(value)}
                          />
                          <Tooltip 
                            formatter={(value: number, name: string) => [`${formatNumber(value)} Ton`, name]}
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0] && payload[0].payload) {
                                return payload[0].payload.fullDate;
                              }
                              return label;
                            }}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e0e0e0',
                              borderRadius: '6px'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="materialIssued" 
                            fill="#3b82f6" 
                            name="Material Issued"
                            radius={[4, 4, 0, 0]}
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-material-${index}`}
                                fill="#3b82f6"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleBarClick(entry)}
                              />
                            ))}
                          </Bar>
                          <Bar 
                            dataKey="scrapWeight" 
                            fill="#f59e0b" 
                            name="Scrap Weight"
                            radius={[4, 4, 0, 0]}
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-scrap-${index}`}
                                fill="#f59e0b"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleBarClick(entry)}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Summary: scrap from scrap API (scrapWeight g→ton), material issued from material issue API */}
                    {(() => {
                      let totalScrap = 0;
                      (filteredScrapData || []).forEach((row) => {
                        const grams = Number((row as { scrapWeight?: number }).scrapWeight) || 0;
                        totalScrap += grams * SCRAP_GRAMS_TO_TON;
                      });
                      // issueQuantity from material issue API is in tons (filtered by config category)
                      const totalMaterialIssued = filteredMaterialIssueData.reduce(
                        (sum, item) => sum + (Number(item.issueQuantity) || 0),
                        0
                      );
                      const uniqueDates = chartData.length;
                      const averageScrap = uniqueDates > 0 ? totalScrap / uniqueDates : 0;
                      const averageMaterialIssued = uniqueDates > 0 ? totalMaterialIssued / uniqueDates : 0;
                      
                      return (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <div className="flex items-center justify-center gap-8">
                            <div className="text-center">
                              <div className="text-sm text-gray-600">Average Material Issued per Day</div>
                              <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                                {formatNumber(averageMaterialIssued)} Ton
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-gray-600">Total Material Issued</div>
                              <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                                {formatNumber(totalMaterialIssued)} Ton
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-gray-600">Average Scrap per Day</div>
                              <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>
                                {formatNumber(averageScrap)} Ton
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-gray-600">Total Scrap Generated</div>
                              <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>
                                {formatNumber(totalScrap)} Ton
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Scrap Details Table for selected date */}
                {selectedDate && (
                  <Card className="border-primary-200 bg-white shadow-lg mt-4">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-gray-800">
                        Scrap Details on {selectedDateLabel || selectedDate}
                        {selectedEndgoodName && ` - Filtered by: ${selectedEndgoodName}`}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingDetails ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                          <span className="ml-2 text-gray-600">Loading details...</span>
                        </div>
                      ) : filteredDetails.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead className="font-semibold">Work Order</TableHead>
                                <TableHead className="font-semibold">Endgood Name</TableHead>
                                <TableHead className="font-semibold">Process Name</TableHead>
                                <TableHead className="font-semibold text-right">Completed Qty</TableHead>
                                <TableHead className="font-semibold text-right">Endgood Wt/1000 Qty (Ton)</TableHead>
                                <TableHead className="font-semibold text-right">Reject Qty</TableHead>
                                <TableHead className="font-semibold text-right">Scrap Weight (Ton)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredDetails.map((detail, index) => {
                                const scrapTon = detail.scrapWeight != null
                                  ? detail.scrapWeight * SCRAP_GRAMS_TO_TON
                                  : Number(detail.materialScrapTons) || 0;

                                return (
                                  <TableRow key={detail.workOrderId ? `${detail.workOrderId}-${detail.rawItemId}-${index}` : index} className="hover:bg-gray-50">
                                    <TableCell>
                                      {detail.workOrderId ? (
                                        <Link to={`/work-orders/${detail.workOrderId}/view`} className="text-primary-600 hover:underline">
                                          {detail.workOrderNumber || `WO-${detail.workOrderId}`}
                                        </Link>
                                      ) : (
                                        detail.workOrderNumber || '-'
                                      )}
                                    </TableCell>
                                    <TableCell>{detail.endgoodName || '-'}</TableCell>
                                    <TableCell>{detail.processName ?? '-'}</TableCell>
                                    <TableCell className="text-right">{formatNumber(detail.quantityCompleted || 0)}</TableCell>
                                    <TableCell className="text-right">
                                      {formatTonsPerThousand(detail.endgoodProductionWeightGrams)}
                                    </TableCell>
                                    <TableCell className="text-right">{formatNumber(detail.totalRejectQuantity || 0)}</TableCell>
                                    <TableCell className="text-right font-semibold" style={{ color: '#f59e0b' }}>
                                      {formatNumber(scrapTon)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No scrap details found for this date{selectedEndgoodName ? ` and endgood filter` : ''}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ScrapReportPage;
